'use client';

import { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';

export default function FaqAccordion({ items, variant = 'light' }) {
  const [openId, setOpenId] = useState(null);

  const toggle = (id) => setOpenId((prev) => (prev === id ? null : id));

  return (
    <div className="flex flex-col gap-3.5">
      {items.map((item, index) => {
        const isOpen = openId === item.id;
        const n = String(index + 1).padStart(2, '0');

        return (
          <div
            key={item.id}
            className={`group rounded-2xl border transition-all duration-300 ${
              isOpen
                ? 'border-primary/30 bg-white shadow-md ring-4 ring-primary/5'
                : 'border-slate-200/80 bg-white hover:border-slate-300 hover:shadow-xs'
            }`}
          >
            <button
              type="button"
              onClick={() => toggle(item.id)}
              aria-expanded={isOpen}
              aria-controls={`faq-answer-${item.id}`}
              id={`faq-question-${item.id}`}
              className="flex w-full items-center justify-between gap-4 p-4 text-left outline-none md:p-5 cursor-pointer"
            >
              <span className="flex min-w-0 flex-1 items-center gap-3.5">
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-xs font-extrabold tabular-nums transition-colors ${
                    isOpen
                      ? 'bg-primary text-white shadow-xs'
                      : 'bg-primary/10 text-primary group-hover:bg-primary/20'
                  }`}
                  aria-hidden
                >
                  {n}
                </span>
                <span
                  className={`min-w-0 text-sm font-bold leading-snug sm:text-base transition-colors duration-200 ${
                    isOpen ? 'text-slate-900' : 'text-slate-800 group-hover:text-primary'
                  }`}
                >
                  {item.question}
                </span>
              </span>

              <span
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl transition-all duration-300 ${
                  isOpen
                    ? 'bg-primary/15 text-primary rotate-180'
                    : 'bg-slate-100 text-slate-400 group-hover:bg-slate-200/80 group-hover:text-slate-600'
                }`}
              >
                <ChevronDown className="h-4 w-4" strokeWidth={2.5} />
              </span>
            </button>

            <div
              id={`faq-answer-${item.id}`}
              role="region"
              aria-labelledby={`faq-question-${item.id}`}
              className={`grid transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] ${
                isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
              }`}
            >
              <div className="overflow-hidden">
                <div className="border-t border-slate-100/80 px-4 pb-5 pt-3.5 md:px-5 md:pb-6">
                  <p className="text-xs sm:text-sm leading-relaxed text-slate-600">
                    {item.answer}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
